import React from 'react';
import './Technique.css';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import techbanner from './images/tech_banner.webp';
import sinewave from './images/sinewave.webp';
import stepturn1 from './images/stepturn1.webp';
import stepturn2 from './images/stepturn2.webp';

const Technique = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>TKD Study - Technique</title>
      </Helmet>
      <div className="technique-page">
        <div className="grey-banner">
          <img src={techbanner} alt="Technique Banner" />
        </div>

        <div className="back-arrow" onClick={handleBackClick}>
          ← Back
        </div>

        <div className="technique-section">
          {/* Text Block 1 */}
          <div className="technique-text">
            <big><b>Sine Wave</b></big><br />
            The basics of sine wave are 'down-up-down', in other words there is always a downward motion first, followed by an upward motion, and ending in a downward motion when the technique is executed. <br /><br />
            <b>A: DOWN</b> Both knees bend as the transition to next move begins. <br />
            <b>B: UP</b> Both feet come together. The person reaches the maximum height. The knees never fully lengthen and the feet do not touch. <br />
            <b>C: DOWN</b> The body drops into the final stance. The technique finishes with the stance. <br /><br />
            There are variations with sine wave in different motions, listed below.<br />
          </div>

          {/* Image 1 */}
          <div className="technique-image">
            <img src={sinewave} alt="Sine Wave" />
          </div>

          {/* Text Block 2 */}
          <div className="technique-text">
            <br />
              <big><b>Motions</b></big><br />
              Motions are how you transition between 2 techniques in Taekwon-Do. They can be broken down into the number of movements, sine waves and breaths. <br />
              This can be denoted by (movements - sine waves - breathes) such as (1-1-1) or (2-1-1).
              <br />
              Motions can be complimented with other techniques such as stamping, slipping or pulling.<br /><br />

              <big><b>Normal Motion</b> (1-1-1)</big><br />
              Majority of moves in Taekwon-Do are a 'Normal Motion' with 1 movement, 1 sine wave and 1 breath. All movements in <font color="#AA0000"><b>Saju Jirugi</b></font>, <font color="#AA0000"><b>Saju Makgi</b></font> and <font color="#AA0000"><b>Chon-Ji Tul</b></font> are Normal Motions.<br /><br />

              <big><b>Continuous Motion</b> (2-2-1)</big><br />
              A Continuous Motion is 2 movements performed consecutively, 2 movements, 2 sine waves and 1 breath. Movements 13 and 14 in <font color="#AA0000"><b>Dan-Gun Tul</b></font> are Continuous Motions.<br /><br />

              <big><b>Fast Motion</b> (2-2-2)</big><br />
              A Fast Motion has 2 movements, 1½ sine waves and 2 breaths. The sine wave is 'down-up-down-up-down', there is no pause between movements. Movements 15 & 16 and 19 & 20 in <font color="#AA0000"><b>Do-San Tul</b></font> are Fast Motions.<br /><br />

              <big><b>Connecting Motion</b> (2-1-1)</big><br />
              A Connecting Motion is when 2 movements are executed in 1 sine wave, there is no pause between movements. There is 2 movements, 1 sine waves and 1 elongated breath. Movements 16 & 17 and 19 & 20 in <font color="#AA0000"><b>Yul-Gok Tul</b></font> are Connecting Motions. In a static position this requires you to pivot on the axis of the ball of the foot.<br /><br />

              <big><b>Slow Motion</b> (1-1-1)</big><br />
              A Slow Motion is the same as a Normal Motion but performed slowly, there is no power or acceleration. Slow motion is designed to display balance, timing and breath control. Movements 27, 29 and 30 in <font color="#AA0000"><b>Joong-Gun Tul</b></font> are Slow Motions.<br /><br />

              <b>There is also 3 other movements that are not motions:</b><br /><br />

              <big><b>Natural Movement</b></big><br />
              Natural Movements are the same as Normal Motions but do not have a snap or abrupt finish and can be described as soft. Movements 15 & 18 in <font color="#AA0000"><b>Yul-Gok Tul</b></font> are Natural Movements.<br /><br />

              <big><b>Stamping Movement</b></big><br />
              Stamping Movements are used to emphasise power and accentuate the hip movement, the side sole or under the balkal is used for stamping. Movements 13 - 18 in <font color="#AA0000"><b>Toi-Gye Tul</b></font> are Stamping Movements.<br /><br />

              <big><b>Releasing Movement</b></big><br />
              Releasing Movements are done to simulate releasing from a grab. The grabbed body part is rotated with 1 sine wave with no backwards motion. Movements 15 and 18 in <font color="#AA0000"><b>Joong-Gun Tul</b></font> are Releasing Movements.<br /><br /><br />

              <b>There are five alternative ways to transition into another stance aside from direct movement:</b><br /><br />

              <big><b>Step Turning</b></big><br />
              To Step Turn (1.) the front foot (A) crosses the centre line. Twist the front foot (A) 90° inwards while stepping on the ball of the foot. (2.) The rear foot (B) crosses over the centre line and twists 90° landing on the ball of the foot and then (3.) pivot both feet another 90° twisting into the next stance.
              <br /><br />
              The next technique should finish when both feet are fully placed. Step-turning is used to turn to face an opponent in any direction. In <font color="#AA0000"><b>Do-San Tul</b></font> the transition from Movements from 2 to 3 is a Step Turn.<br /><br />
          </div>

          <div className="technique-image-row">
            <img src={stepturn1} alt="Step Turn 1" />
            <img src={stepturn2} alt="Step Turn 2" />
          </div>

          {/* Text Block 3 */}
          <div className="technique-text">
            <big><b>Slipping the Foot</b></big><br />
            To Slip the Foot is to transition from a shorter stance to a longer stance. The weight is placed on the stationary foot and the moving foot glides along the ground into the next stance. The Movements 8 and 10 in <font color="#AA0000"><b>Joong-Gun Tul</b></font> are Slipped from <font color="#0000AA"><b>L-Stance</b></font> to <font color="#0000AA"><b>Walking Stance</b></font>.<br /><br />

            <big><b>Sliding</b></big><br />
            Sliding used to change stance and also gain distance. Using the rear foot to push off the ground and slide forwards half a shoulder width into the next stance. In <font color="#AA0000"><b>Hwa-Rang Tul</b></font> the transition from Movements from 5 to 6 is a Slide.<br /><br />

            <big><b>Pulling the Foot</b></big><br />
            Pulling the Foot is to transition from a longer stance to a shorter stance. The weight is placed on the stationary foot and the moving foot glides along the ground as is pulled into the next stance. The Movements 6 & 7 and 11 & 12 in <font color="#AA0000"><b>Hwa-Rang Tul</b></font> are Pulled.<br /><br />

            <big><b>Double Stepping</b></big><br />
            Double Stepping is done to cover move distance, both steps with full sine wave. Movements 4 and 6 in <font color="#AA0000"><b>Kwang-Gae Tul</b></font> are Double Stepping Motion.
          </div>

          {/* Ad Placeholder */}
          <div className="ad-placeholder">
            {/* Replace with your real ad code */}
            <p>Ad goes here</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Technique;
